// Generic 1080×1080 share card for any guide (North Star is one spec of this).
// Layout: brand · eyebrow · title · lede · red alert strip · "the tell" · colored footer with two lines.

const INK = "#1A1A1A";
const BODY = "#3a3a3a";
const RED = "#B91C1C";

const wrapLines = (ctx, text, maxWidth) => {
  const words = String(text || "").split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const candidate = line ? `${line} ${w}` : w;
    if (ctx.measureText(candidate).width > maxWidth && line) { lines.push(line); line = w; }
    else line = candidate;
  }
  if (line) lines.push(line);
  return lines;
};

const drawLines = (ctx, lines, x, y, lh) => {
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lh));
  return y + lines.length * lh;
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawShield = (ctx, x, y, s, color) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.moveTo(0, -s);
  ctx.lineTo(s * 0.85, -s * 0.65);
  ctx.quadraticCurveTo(s * 0.9, s * 0.4, 0, s);
  ctx.quadraticCurveTo(-s * 0.9, s * 0.4, -s * 0.85, -s * 0.65);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = `700 ${s * 1.1}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("!", 0, -s * 0.05);
  ctx.restore();
};

const hexToRgba = (hex, a) => {
  const h = (hex || "#1D4ED8").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const strip = (s) => String(s || "").replace(/\*\*|\*/g, "").replace(/\[([^\]]+)\]\([^)]*\)(\{#[0-9a-fA-F]{3,6}\})?/g, "$1");

export const renderGuideCard = async (spec) => {
  const color = spec.color || "#1D4ED8";
  try {
    await Promise.all([document.fonts.load("700 84px 'Playfair Display'"), document.fonts.load("600 31px 'Playfair Display'")]);
  } catch { /* fallback fonts */ }
  const W = 1080, H = 1080, M = 72;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const maxW = W - M * 2;

  ctx.fillStyle = hexToRgba(color, 0.08);
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, 28, 28, W - 56, H - 56, 34);
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = hexToRgba(color, 0.35);
  ctx.stroke();

  ctx.textBaseline = "top";
  ctx.fillStyle = "#5C5C5C";
  ctx.font = "700 22px 'Playfair Display', Georgia, serif";
  ctx.textAlign = "right";
  ctx.fillText(spec.brand || "THE WALL · A Guide", W - M, M - 10);
  ctx.textAlign = "left";

  drawShield(ctx, M + 22, M + 48, 22, color);
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillStyle = color;
  ctx.font = "800 24px Arial, sans-serif";
  ctx.letterSpacing = "4px";
  let y = drawLines(ctx, wrapLines(ctx, strip(spec.eyebrow).toUpperCase(), maxW - 70), M + 60, M + 34, 32);
  ctx.letterSpacing = "0px";

  y += 22;
  ctx.fillStyle = INK;
  ctx.font = "700 84px 'Playfair Display', Georgia, serif";
  let titleLines = wrapLines(ctx, strip(spec.title), maxW);
  if (titleLines.length > 2) { ctx.font = "700 64px 'Playfair Display', Georgia, serif"; titleLines = wrapLines(ctx, strip(spec.title), maxW); y = drawLines(ctx, titleLines, M, y, 72); }
  else y = drawLines(ctx, titleLines, M, y, 92);

  y += 26;
  ctx.fillStyle = BODY;
  ctx.font = "400 31px Arial, sans-serif";
  y = drawLines(ctx, wrapLines(ctx, strip(spec.lede), maxW).slice(0, 4), M, y, 42);

  if (spec.alert_title || spec.alert_body) {
    y += 32;
    const bodyLines = wrapLines(ctx, strip(spec.alert_body), maxW - 60).slice(0, 3);
    const ah = 56 + bodyLines.length * 34 + 16;
    ctx.fillStyle = "#FEF2F2";
    roundRect(ctx, M, y, maxW, ah, 18);
    ctx.fill();
    ctx.fillStyle = RED;
    ctx.fillRect(M, y + 12, 8, ah - 24);
    ctx.font = "800 26px Arial, sans-serif";
    ctx.fillText(strip(spec.alert_title).toUpperCase(), M + 30, y + 20);
    ctx.fillStyle = INK;
    ctx.font = "400 27px Arial, sans-serif";
    drawLines(ctx, bodyLines, M + 30, y + 56, 34);
    y += ah;
  }

  if (spec.tell_body) {
    y += 34;
    ctx.fillStyle = color;
    ctx.font = "800 22px Arial, sans-serif";
    ctx.letterSpacing = "3px";
    ctx.fillText(strip(spec.tell_label || "REMEMBER").toUpperCase(), M, y);
    ctx.letterSpacing = "0px";
    ctx.fillStyle = INK;
    ctx.font = "600 31px 'Playfair Display', Georgia, serif";
    drawLines(ctx, wrapLines(ctx, strip(spec.tell_body), maxW).slice(0, 3), M, y + 32, 42);
  }

  const fh = 150;
  const fy = H - 28 - fh;
  ctx.fillStyle = color;
  roundRect(ctx, 28, fy, W - 56, fh, 34);
  ctx.fill();
  ctx.fillRect(28, fy, W - 56, 40);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 32px Arial, sans-serif";
  const fl = drawLines(ctx, wrapLines(ctx, strip(spec.footer_line), maxW).slice(0, 2), M, fy + 30, 40);
  ctx.fillStyle = "#F2CC8F";
  ctx.font = "800 32px Arial, sans-serif";
  ctx.fillText(strip(spec.link_line), M, fl + 12);
  return canvas;
};

const toBlob = (canvas) => new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));

const triggerDownload = (blob, file) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = file;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const downloadGuideCard = async (spec, file) => triggerDownload(await toBlob(await renderGuideCard(spec)), file);

export const shareGuideCard = async (spec, file, text) => {
  const blob = await toBlob(await renderGuideCard(spec));
  if (!blob) return { shared: false, method: "none" };
  const f = new File([blob], file, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [f] })) {
    try {
      await navigator.share({ files: [f], title: strip(spec.title), text });
      return { shared: true, method: "native" };
    } catch { return { shared: false, method: "cancelled" }; }
  }
  triggerDownload(blob, file);
  return { shared: true, method: "download" };
};
